export interface OneEuroOptions {
  mincutoff?: number;
  beta?: number;
  dcutoff?: number;
}

function smoothingAlpha(cutoff: number, dtSec: number): number {
  const tau = 1 / (2 * Math.PI * cutoff);
  return 1 / (1 + tau / dtSec);
}

function lowpass(x: number, prev: number, a: number): number {
  return a * x + (1 - a) * prev;
}

export class OneEuroFilter {
  private mincutoff: number;
  private beta: number;
  private dcutoff: number;
  private prevValue = Number.NaN;
  private prevDerivative = 0;
  private prevTime = 0;

  constructor(opts: OneEuroOptions = {}) {
    this.mincutoff = opts.mincutoff ?? 1;
    this.beta = opts.beta ?? 0.1;
    this.dcutoff = opts.dcutoff ?? 1;
  }

  reset(): void {
    this.prevValue = Number.NaN;
    this.prevDerivative = 0;
    this.prevTime = 0;
  }

  setParams(opts: OneEuroOptions): void {
    if (opts.mincutoff !== undefined) this.mincutoff = Math.max(0.001, opts.mincutoff);
    if (opts.beta !== undefined) this.beta = Math.max(0, opts.beta);
    if (opts.dcutoff !== undefined) this.dcutoff = Math.max(0.001, opts.dcutoff);
  }

  filter(value: number, tMs: number): number {
    if (!Number.isFinite(this.prevValue)) {
      this.prevValue = value;
      this.prevDerivative = 0;
      this.prevTime = tMs;
      return value;
    }
    const dtSec = Math.max(1e-3, (tMs - this.prevTime) / 1000);
    const dx = (value - this.prevValue) / dtSec;
    const aD = smoothingAlpha(this.dcutoff, dtSec);
    const dxHat = lowpass(dx, this.prevDerivative, aD);
    const cutoff = this.mincutoff + this.beta * Math.abs(dxHat);
    const a = smoothingAlpha(cutoff, dtSec);
    const xHat = lowpass(value, this.prevValue, a);
    this.prevValue = xHat;
    this.prevDerivative = dxHat;
    this.prevTime = tMs;
    return xHat;
  }
}
